import Fuse, {type IFuseOptions} from "fuse.js";

import {Permissions, RoleData} from "@/enums/user";
import {
  ROUTE_REGISTRY,
  RouteEntry,
  translateTags,
} from "@/data/routeRegistry";
import {canAccessNavItem, type NavAccessFields} from "@/utils/authz/navAccess";
import {decideRouteAccess} from "@/utils/authz/routeAccess";
import {matchRouteGateEntry, NOT_FOUND_PATTERN} from "@/utils/authz/routeGates";
import {
  canSeeReportingNav,
  getReportingNavHref,
} from "@/utils/reporting/access";

export type SearchableNavItem = {
  icon: string;
  label: string;
  href: string;
  tags: string[];
  name: string;
  description: string;
};

export type SearchableRouteContext = {
  canAccessAny: (dataTypes: readonly RoleData[], level: Permissions) => boolean;
  isAuthenticated: boolean;
  agencyId: string | undefined | null;
  permissionsDegraded: boolean;
  provisioningPending: boolean;
  hasPermissions: boolean;
  userCanUseGailGpt: boolean;
  userCanUseGailAgent: boolean;
  productAccessLoading: boolean;
  gailAgentEnabled: boolean;
  isReportUser: boolean;
  orgOutcomesEnabled: boolean;
  t: (key: string) => string;
  ct: (key: string) => string;
  supportHref: string;
};

const SEARCH_EXCLUDED_PATHS = new Set(["/get-gail", "/upgrade", "/smart-onboarding"]);

const DEFAULT_SEARCH_ICON = "file";

function navAccessFields(route: RouteEntry): NavAccessFields | undefined {
  if (!route.nav) return undefined;
  return {
    dataType: route.nav.dataType,
    additionalDataTypes: route.nav.additionalDataTypes,
    levels: route.nav.levels,
  };
}

function searchTrustsPermissions(ctx: SearchableRouteContext): boolean {
  return (
    ctx.isAuthenticated &&
    !ctx.permissionsDegraded &&
    !ctx.provisioningPending &&
    ctx.hasPermissions
  );
}

function pageIsSearchable(route: RouteEntry, ctx: SearchableRouteContext): boolean {
  if (!route.path.startsWith("/")) {
    const access = navAccessFields(route);
    return Boolean(access && canAccessNavItem(access, ctx.canAccessAny));
  }

  const entry = matchRouteGateEntry(route.path);
  if (!entry || entry.pattern === NOT_FOUND_PATTERN) return false;

  return (
    decideRouteAccess({
      pathname: route.path,
      isAuthenticated: ctx.isAuthenticated,
      agencyId: ctx.agencyId,
      permissionsDegraded: ctx.permissionsDegraded,
      provisioningPending: ctx.provisioningPending,
      hasPermissions: ctx.hasPermissions,
      canAccessAny: ctx.canAccessAny,
    }).verdict === "allow"
  );
}

export function canSearchRoute(
  route: RouteEntry,
  ctx: SearchableRouteContext
): boolean {
  if (SEARCH_EXCLUDED_PATHS.has(route.path)) return false;
  // Search is fail-closed: skip means "we don't know", not "show it" like the page guard.
  if (!searchTrustsPermissions(ctx)) return false;
  if (
    route.search?.requiresGailAgent &&
    !ctx.userCanUseGailAgent &&
    !ctx.productAccessLoading
  ) {
    return false;
  }
  if (route.search?.requiresOrgGailAgent && !ctx.gailAgentEnabled) return false;
  if (route.search?.requiresOutcomesFlag && !ctx.orgOutcomesEnabled) return false;

  const navDataType = route.nav?.dataType;
  if (navDataType === RoleData.GPT && !ctx.userCanUseGailGpt) return false;
  if (
    navDataType === RoleData.Reporting &&
    !canSeeReportingNav(
      ctx.gailAgentEnabled,
      ctx.isReportUser,
      ctx.canAccessAny([RoleData.Reporting], Permissions.Read)
    )
  ) {
    return false;
  }

  if (!pageIsSearchable(route, ctx)) return false;

  if (route.search?.access && !canAccessNavItem(route.search.access, ctx.canAccessAny)) {
    return false;
  }

  return true;
}

function routeSearchHref(route: RouteEntry, ctx: SearchableRouteContext): string {
  if (route.nav?.labelKey === "support") return ctx.supportHref;
  if (route.path === "/reporting") {
    return getReportingNavHref(ctx.gailAgentEnabled, ctx.isReportUser);
  }
  return route.path;
}

function routeSearchLabel(route: RouteEntry, ctx: SearchableRouteContext): string {
  if (route.search?.labelKey) return ctx.t(route.search.labelKey);
  if (route.nav?.labelKey === "reporting") return ctx.ct("reporting");
  if (route.nav?.labelKey) return ctx.t(route.nav.labelKey);
  return route.name;
}

export function toSearchableNavItem(
  route: RouteEntry,
  ctx: SearchableRouteContext
): SearchableNavItem {
  return {
    icon: route.search?.icon ?? route.nav?.icon ?? DEFAULT_SEARCH_ICON,
    label: routeSearchLabel(route, ctx),
    href: routeSearchHref(route, ctx),
    tags: translateTags(route.tagKeys, ctx.t),
    name: route.name,
    description: route.description,
  };
}

export function buildSearchableNavItems(
  ctx: SearchableRouteContext,
  routes: readonly RouteEntry[] = ROUTE_REGISTRY
): SearchableNavItem[] {
  return routes
    .filter((route) => canSearchRoute(route, ctx))
    .map((route) => toSearchableNavItem(route, ctx));
}

const NAV_FUSE_OPTIONS: IFuseOptions<SearchableNavItem> = {
  // Description is English marketing copy for Ask Gail. Indexing it makes
  // "business" hit Organization Details ("business details") instead of Hours.
  keys: ["label", "name", "href", "tags"],
  ignoreLocation: true,
  useExtendedSearch: true,
  threshold: 0,
};

function fuseQuery(query: string): string | null {
  const tokens = query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .map((token) => token.replace(/['|^$=!\\]/g, ""))
    .filter(Boolean);
  if (tokens.length === 0) return null;
  return tokens
    .map((token) => {
      const singular = token.length > 3 && token.endsWith("s") ? token.slice(0, -1) : null;
      return singular ? `'${token} | '${singular}` : `'${token}`;
    })
    .join(" ");
}

export function navItemMatchesQuery(item: SearchableNavItem, query: string): boolean {
  const pattern = fuseQuery(query);
  if (!pattern) return false;
  return new Fuse([item], NAV_FUSE_OPTIONS).search(pattern).length > 0;
}

export function filterSearchableNavItems(
  items: SearchableNavItem[],
  query: string,
  limit = 6
): SearchableNavItem[] {
  const pattern = fuseQuery(query);
  if (!pattern) return [];
  return new Fuse(items, NAV_FUSE_OPTIONS)
    .search(pattern)
    .slice(0, limit)
    .map((result) => result.item);
}
